import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Chip } from "../../components/ui/primitives";
import { cn } from "../../lib/cn";
import { fmtDateTime, fmtDuration, relTime } from "../../lib/format";
import { agentStatusText, PHASE_LABEL, RUN_HELP, RUN_LABEL, RUN_TONE, type RunRow } from "./model";

export const ACTIVE = new Set(["creating", "running", "waiting"]);

export const RunStatusChip = ({ status, className }: { status: string; className?: string }) => (
  <Chip tone={RUN_TONE[status] ?? "neutral"} title={RUN_HELP[status]} className={className}>{RUN_LABEL[status] ?? status}</Chip>
);

/** Duración de un run: la registrada, o el tiempo transcurrido si sigue vivo. Sin dato → «—», nunca 0. */
export function runDuration(r: Pick<RunRow, "durationMs" | "startedAt" | "status">, now: number): string {
  if (r.durationMs != null) return fmtDuration(r.durationMs);
  if (ACTIVE.has(r.status) && r.startedAt) return `${fmtDuration(now - new Date(r.startedAt).getTime())} (en curso)`;
  return "—";
}

const th = "px-2 py-2.5 text-left md:px-3 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-mu";
const td = "px-2 py-3 align-top md:px-3";

/**
 * Tabla compacta: una fila por run. En pantallas estrechas solo quedan misión, repositorio, fase y estado
 * (el resto se consulta en el detalle).
 */
export function RunsTable({ rows, now, onOpen, selectedId }: { rows: RunRow[]; now: number; onOpen: (id: string) => void; selectedId: string }) {
  return (
    <div className="overflow-x-auto rounded-card border border-ln bg-sf">
      <table className="w-full min-w-0 border-collapse text-[13px]">
        <thead className="border-b border-ln">
          <tr>
            <th className={th}>Misión</th>
            <th className={th}><span className="md:hidden">Repo · fase</span><span className="hidden md:inline">Repositorio</span></th>
            <th className={cn(th, "hidden md:table-cell")}>Fase</th>
            <th className={cn(th, "hidden lg:table-cell")}>Proveedor</th>
            <th className={th}>Estado</th>
            <th className={cn(th, "hidden xl:table-cell")}>Inicio</th>
            <th className={cn(th, "hidden md:table-cell")}>Duración</th>
            <th className={cn(th, "hidden lg:table-cell")}>Últ. actualización</th>
            <th className={cn(th, "w-7 !px-1 md:w-10")}><span className="sr-only">Detalle</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const agentNote = r.agent.archived || r.agent.status !== "ACTIVE" ? (r.agent.archived ? "archivado" : agentStatusText(r.agent.status)) : null;
            return (
              <tr key={r.id} onClick={() => onOpen(r.id)} data-run-id={r.id} className={cn("cursor-pointer border-t border-ln first:border-t-0 hover:bg-hv", selectedId === r.id && "bg-hv")}>
                <td className={cn(td, "max-w-[84px] md:max-w-[260px]")}>
                  <Link to={`/missions/${r.missionId}`} onClick={(e) => e.stopPropagation()} className="font-mono font-semibold text-ok hover:underline">{r.missionKey}</Link>
                  <div className="truncate text-mu" title={r.missionTitle}>{r.missionTitle}</div>
                </td>
                <td className={cn(td, "min-w-[100px] max-w-[200px] break-all font-mono text-[11px] md:break-words md:text-[12px]")}>{r.repository}<div className="mt-1 font-body text-[12px] text-tx md:hidden">{PHASE_LABEL[r.purpose] ?? r.purpose}</div></td>
                <td className={cn(td, "hidden md:table-cell")}>{PHASE_LABEL[r.purpose] ?? r.purpose}</td>
                <td className={cn(td, "hidden font-mono text-[12px] lg:table-cell")}>{r.provider}</td>
                <td className={td}>
                  <RunStatusChip status={r.status} className="max-md:h-auto max-md:whitespace-normal max-md:py-1 max-md:text-[12px] max-md:leading-tight" />
                  {r.pendingConfirmation && ACTIVE.has(r.status) && <div className="mt-1 text-[12px] text-mu">sin confirmar</div>}
                  {agentNote && <div className="mt-1 text-[12px] text-mu" title="Estado del agente persistente, distinto del estado del run">Agente {agentNote}</div>}
                </td>
                <td className={cn(td, "hidden whitespace-nowrap xl:table-cell")}>{r.startedAt ? fmtDateTime(r.startedAt) : "sin iniciar"}</td>
                <td className={cn(td, "hidden whitespace-nowrap md:table-cell")}>{runDuration(r, now)}</td>
                <td className={cn(td, "hidden whitespace-nowrap lg:table-cell")} title={fmtDateTime(r.lastUpdateAt)}>{relTime(r.lastUpdateAt, now)}</td>
                <td className={cn(td, "text-mu")}>
                  <button type="button" aria-label={`Ver detalle de ${r.missionKey} · ${PHASE_LABEL[r.purpose] ?? r.purpose}`} onClick={(e) => { e.stopPropagation(); onOpen(r.id); }}
                    className="flex h-9 w-6 cursor-pointer items-center justify-center rounded-lg hover:text-tx md:w-9"><ChevronRight className="h-4 w-4" /></button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
